import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import { withRouter } from 'react-router-dom'
import yup from 'yup'
import pick from 'lodash/pick'
import uniq from 'lodash/uniq'

import Button from '@components/Button'
import Tooltip from '@components/Tooltip/Tooltip'
import { TagsInput } from '@components/Form/Autocomplete'
import Checkbox from '@components/Form/Checkbox'
import Input from '@components/Form/Input'
import ScreeningQuestionFields from '@components/Form/ScreeningQuestionFields'
import TextArea from '@components/Form/TextArea'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import Spinner from '@components/spinner'

import SegmentFormStore from './segmentForm.store'
import { defaultScreeningQuestion, defaultExpertBrief, defaultDisclaimer1, defaultDisclaimer2 } from './formDefaults'

class SegmentForm extends Component {
  static propTypes = {
    staticData: PropTypes.object.isRequired,
    eplStore: PropTypes.object.isRequired,
    modalStore: PropTypes.object.isRequired,
    project: PropTypes.object.isRequired,
    segment: PropTypes.object,
    segmentStore: PropTypes.object,
    expertToProjectFormStore: PropTypes.object,
    history: PropTypes.object
  }

  state = { error: null }

  constructor(props) {
    super(props)
    extendObservable(this, {
      segmentFormStore: new SegmentFormStore(
        props.staticData,
        props.segment,
        props.project
      )
    })
  }

  getInitialValues() {
    const { segment } = this.props

    if (segment) {
      const relevantFields = [
        'complete',
        'name',
        'numberOfExpert',
        'expertBrief',
        'disclaimer1',
        'disclaimer2'
      ]
      const formData = pick(segment, relevantFields)

      // Some segments have null for this field's value
      formData.complete = !!formData.complete
      const employees = segment.employees.map(e => {
        return {
          fullName: e.fullName,
          id: e.id
        }
      })

      let screeningQuestions
      if (segment.screeningQuestions.length) {
        screeningQuestions = segment.screeningQuestions.map(sq => ({
          id: sq.id,
          question: sq.question
        }))
      } else {
        screeningQuestions = [{ question: defaultScreeningQuestion }]
      }

      if (!formData.disclaimer1) formData.disclaimer1 = defaultDisclaimer1
      if (!formData.disclaimer2) formData.disclaimer2 = defaultDisclaimer2

      return {
        ...formData,
        employees,
        screeningQuestions
      }
    } else {
      return {
        complete: false,
        name: '',
        numberOfExpert: '',
        employees: [],
        screeningQuestions: [{ question: defaultScreeningQuestion }],
        expertBrief: defaultExpertBrief,
        disclaimer1: defaultDisclaimer1,
        disclaimer2: defaultDisclaimer2
      }
    }
  }

  getFormValidationSchema = () => {
    const { segment, segmentStore } = this.props
    let employees = yup.array().of(yup.object())
    if (this.props.expertToProjectFormStore) {
      employees = employees.min(
        1,
        'At least one associate is required when inviting experts'
      )
    }

    return yup.object().shape({
      complete: yup.boolean().default(false),
      name: yup
        .string()
        .required('Name is required')
        .test(
          'unique',
          'Segment name is already used on this project',
          value => {
            if (
              yup
                .string()
                .required()
                .isValidSync(value)
            ) {
              if (!segmentStore) return true
              const segmentName = value.trim().toLowerCase()
              return !segmentStore.segments.find(seg =>
                segment && segment.id
                  ? seg.name.toLowerCase() === segmentName &&
                  seg.id !== segment.id
                  : seg.name.toLowerCase() === segmentName
              )
            }
          }
        ),
      numberOfExpert: yup
        .number()
        .integer('No. of experts must a whole number')
        .typeError('No. of experts must be a number')
        .required('No. of experts is required'),
      employees,
      expertBrief: yup
        .string()
        .required('Expert brief is required')
        .test(
          'isNotDefault',
          'Please provide a short brief of client requirements for this segment.',
          value => value !== defaultExpertBrief
        )
        .nullable(),
      disclaimer1: yup
        .string()
        .required('Disclaimer 1 is required')
        .nullable(),
      disclaimer2: yup
        .string()
        .required('Disclaimer 2 is required')
        .nullable(),
      screeningQuestions: yup.array().of(
        yup.object().shape({
          question: yup.string().required('Screening question is required')
        })
      ),
      screeningQuestionsNotRepeating: yup
        .mixed()
        .test(
          'screeningQuestionsNotRepeating',
          'You have duplicate screening questions',
          function () {
            const screeningQuestions = this.parent.screeningQuestions.map(
              sq => sq.question
            )
            const uniqueScreeningQuestions = uniq(screeningQuestions)
            return uniqueScreeningQuestions.length === screeningQuestions.length
          }
        )
    })
  }

  handleFormSubmission = async (values, actions) => {
    this.setState({ error: null })
    const {
      segmentStore,
      eplStore,
      modalStore,
      expertToProjectFormStore,
      segment,
      history,
      formValues
    } = this.props
    const { project } = this.segmentFormStore
    values.projectId = project.id

    try {
      await this.saveSegment(values)

      if (!expertToProjectFormStore) {
        segmentStore.getAll(project.id)
        eplStore.getFilterOptions(project.id)
        modalStore.close()
      } else {
        const senderIds = values.employees.map(e => e.id)
        modalStore.close()
        expertToProjectFormStore.openInvitationModal(
          senderIds,
          segment.id,
          history,
          formValues,
          project.projectName,
          this.props.resetPinned
        )
      }
    } catch (error) {
      this.setState({ error: error.message })
      actions.setSubmitting(false)
    }
  }

  saveSegment = segmentData => {
    const { segment } = this.props
    return segment && segment.id
      ? this.segmentFormStore.update(segment.id, segmentData)
      : this.segmentFormStore.create(segmentData)
  }

  getTitle = () => {
    const { expertToProjectFormStore, segment } = this.props

    if (expertToProjectFormStore) return 'Review segment'
    if (segment && segment.id) return 'Edit segment'
    return 'Add new segment'
  }

  render() {
    const {
      modalStore,
      expertToProjectFormStore,
      project,
      segment
    } = this.props

    if (this.segmentFormStore.loading) return <Spinner />

    return (
      <div>
        <Formik
          initialValues={this.getInitialValues()}
          validationSchema={this.getFormValidationSchema()}
          onSubmit={this.handleFormSubmission}
          render={form => (
            <div className="expert-form">
              <Form onSubmit={form.handleSubmit}>
                <div className="pull-right">
                  <Field
                    name="complete"
                    label="Complete"
                    component={Checkbox}
                  />
                </div>
                <h1 className="expert-form__title">{this.getTitle()}</h1>
                {this.state.error ? (
                  <InfoBox theme="red" icon={<WarningIcon />}>
                    {this.state.error}
                  </InfoBox>
                ) : null}
                <div className="expert-form__section">
                  <Field
                    autoFocus={!(segment && segment.id)}
                    name="name"
                    label="Segment title"
                    component={Input}
                  />
                  <Field
                    name="numberOfExpert"
                    label="No. of experts required"
                    component={Input}
                  />
                  <Field
                    form={form}
                    name="employees"
                    label="Assigned associates"
                    options={this.segmentFormStore.employees}
                    itemToString={item => item && item.fullName}
                    component={TagsInput}
                  />
                  <Field
                    name="expertBrief"
                    label={
                      <span>
                        Expert brief
                        <Tooltip tooltip="A summary of the client requirements for this segment. This part will be included in the e-mail invitation and consultation application page for the experts." />
                      </span>
                    }
                    placeholder={`e.g. We are working on a project for a client who is looking at ${project.projectName}. In connection with this, our client is trying to gain a better understanding of market trends and key players.`}
                    component={TextArea}
                  />
                  <Field
                    name="disclaimer1"
                    label={
                      <span>
                        Disclaimer 1
                      </span>
                    }
                    component={TextArea}
                  />
                  <Field
                    name="disclaimer2"
                    label={
                      <span>
                        Disclaimer 2
                      </span>
                    }
                    component={TextArea}
                  />
                  <hr className="hr-separator--form" />
                  <ScreeningQuestionFields
                    form={form}
                    name="screeningQuestions"
                    label={
                      <span>
                        Screening questions
                        <Tooltip tooltip="At least one question needs to be answered by the experts during project application." />
                      </span>
                    }
                    screeningQuestionsNotRepeating={
                      form.errors.screeningQuestionsNotRepeating
                    }
                  />
                </div>
                <div className="expert-form__section p-b-0">
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}>
                    {expertToProjectFormStore ? 'Next' : 'Save'}
                  </Button>
                  {expertToProjectFormStore ? (
                    <Button
                      onClick={() => {
                        modalStore.close()
                        this.props.modalStore.show({
                          type: 'EXPERT_TO_PROJECT_FORM',
                          props: { expertToProjectFormStore, experts: expertToProjectFormStore.expertList }
                        })
                      }}
                      disabled={form.isSubmitting}>
                      Back
                    </Button>
                  ) : (
                    <Button
                      onClick={modalStore.close}
                      disabled={form.isSubmitting}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

SegmentForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: true
}

export default withRouter(
  inject(
    'modalStore',
    'eplStore',
    'staticData',
    'growlStore'
  )(observer(SegmentForm))
)
